import { useState } from 'react';
import { QUESTIONS, BUDGET_CONFIG, type SvarMulighed } from '@/constants/questions';
import { beregnBesparelse } from '@/lib/calculator';
import { LandingPage } from './LandingPage';
import { BudgetSlider } from './BudgetSlider';
import { QuestionCard } from './QuestionCard';
import { EmailCapture } from './EmailCapture';
import { ResultCard } from './ResultCard';

// Flow: landing → spoergsmaal → budget → email → resultat
type Trin = 'landing' | 'spoergsmaal' | 'budget' | 'email' | 'resultat';

export function Calculator() {
  const [trin, setTrin] = useState<Trin>('landing');
  const [budget, setBudget] = useState<number>(BUDGET_CONFIG.default);
  const [svar, setSvar] = useState<Record<string, SvarMulighed>>({});
  const [spørgsmålIndex, setSpørgsmålIndex] = useState(0);

  const currentQuestion = QUESTIONS[spørgsmålIndex];

  function handleSvar(valg: SvarMulighed) {
    if (!currentQuestion) return;
    const nytSvar = { ...svar, [currentQuestion.key]: valg };
    setSvar(nytSvar);

    if (spørgsmålIndex < QUESTIONS.length - 1) {
      setSpørgsmålIndex(spørgsmålIndex + 1);
    } else {
      setTrin('budget');
    }
  }

  function gåTilbage() {
    if (trin === 'spoergsmaal' && spørgsmålIndex > 0) {
      setSpørgsmålIndex(spørgsmålIndex - 1);
    } else if (trin === 'spoergsmaal') {
      setTrin('landing');
      setSpørgsmålIndex(0);
    } else if (trin === 'budget') {
      setSpørgsmålIndex(QUESTIONS.length - 1);
      setTrin('spoergsmaal');
    }
  }

  const resultat = trin === 'resultat' ? beregnBesparelse(budget, svar) : null;

  // Landing-siden fylder hele skærmen — intet card-wrapper
  if (trin === 'landing') {
    return <LandingPage onStart={() => setTrin('spoergsmaal')} />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Lille header over calculator-kortet */}
      {trin !== 'resultat' && (
        <header className="px-4 pt-10 pb-4 text-center max-w-2xl mx-auto">
          <p className="text-sm font-medium text-brand uppercase tracking-widest">
            Drømmevillaen
          </p>
        </header>
      )}

      <main className="px-4 pb-16 max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">

          {/* Spørgsmål */}
          {trin === 'spoergsmaal' && currentQuestion && (
            <div className="space-y-6">
              <QuestionCard
                question={currentQuestion}
                currentIndex={spørgsmålIndex}
                totalCount={QUESTIONS.length}
                selectedSvar={svar[currentQuestion.key]}
                onSvar={handleSvar}
              />
              <button
                type="button"
                onClick={gåTilbage}
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors
                  focus-visible:outline-none focus-visible:underline"
              >
                ← Tilbage
              </button>
            </div>
          )}

          {/* Budget — efter spørgsmålene */}
          {trin === 'budget' && (
            <div className="space-y-8">
              <div>
                <p className="text-sm text-gray-400 mb-4">Spørgsmål 8 af 8</p>
                <div className="h-1 bg-gray-100 rounded-full overflow-hidden mb-6"
                  role="progressbar" aria-valuenow={8} aria-valuemin={1} aria-valuemax={8}>
                  <div className="h-full bg-brand rounded-full w-full" />
                </div>
                <h2 className="text-2xl font-serif text-gray-900 mb-2">
                  Hvad er dit samlede byggebudget?
                </h2>
                <p className="text-gray-600 mb-6">
                  Inkl. grund, håndværkere, materialer og uforudsete udgifter.
                </p>
                <BudgetSlider value={budget} onChange={setBudget} />
              </div>

              <button
                type="button"
                onClick={() => setTrin('email')}
                className="w-full py-4 px-6 rounded-xl bg-brand text-white font-semibold
                  hover:bg-brand-600 active:bg-brand-700 transition-colors
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
              >
                Vis mit resultat →
              </button>

              <button
                type="button"
                onClick={gåTilbage}
                className="block text-sm text-gray-400 hover:text-gray-600 transition-colors
                  focus-visible:outline-none focus-visible:underline"
              >
                ← Tilbage
              </button>
            </div>
          )}

          {/* Email-gate */}
          {trin === 'email' && (
            <EmailCapture budget={budget} svar={svar} onSuccess={() => setTrin('resultat')} />
          )}

          {/* Resultat */}
          {trin === 'resultat' && resultat && (
            <ResultCard budget={budget} svar={svar} resultat={resultat} />
          )}
        </div>
      </main>

      <footer className="border-t border-gray-100 py-8 text-center text-sm text-gray-400">
        <p>Playground Institute ApS — CVR 36406356</p>
        <p className="mt-1">
          <a href="mailto:morten@droemmevillaen.dk" className="hover:text-brand transition-colors">
            morten@droemmevillaen.dk
          </a>
        </p>
      </footer>
    </div>
  );
}
